import { ThemeEvents } from '@theme/events';

// Product Information Tabs
class ProductInformationTabs extends HTMLElement {
  static ACTIVE_TAB_STORAGE_KEY = 'vb:pdp:active-information-tab';
  static ACCORDION_ONLY_CLASS = 'information-tabs--accordion-only';

  /** @type {boolean} */
  #isSyncingState = false;

  connectedCallback() {
    // In accordion-only mode the tab buttons and panels are hidden at every
    // breakpoint, so there is nothing to control — and marking a panel active
    // would resurrect a duplicate copy of the tab content.
    if (this.classList.contains(ProductInformationTabs.ACCORDION_ONLY_CLASS)) return;

    this.#init();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const eventTarget = event.target instanceof Element ? event.target : null;
    const button = eventTarget?.closest('.tab-button');
    if (!button) return;
    const buttons = Array.from(this.querySelectorAll('.tab-button'));
    const index = buttons.indexOf(button);
    if (index === -1) return;
    // morph strips tabs-initialized from the class attribute since it's not
    // in the server-rendered HTML — re-add it before showing the tab
    this.classList.add('tabs-initialized');
    this.showTab(index);
  };

  #handleVariantUpdate = () => {
    this.#syncFromDom();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (!(event instanceof KeyboardEvent)) return;
    const eventTarget = event.target instanceof Element ? event.target : null;
    const button = eventTarget?.closest('.tab-button');
    if (!button) return;
    const buttons = Array.from(this.querySelectorAll('.tab-button'));
    const index = buttons.indexOf(button);
    if (index !== -1) this.handleKeyDown(event, index);
  };

  #init() {
    const { buttons, panels } = this.#getTabElements();

    if (buttons.length === 0 || panels.length === 0) return;

    this.classList.add('tabs-initialized');
    this.showTab(this.#getPreferredTabIndex(this.#getDefaultIndex()));
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} currentIndex
   */
  handleKeyDown(event, currentIndex) {
    const { buttons } = this.#getTabElements();
    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
        break;
      case 'ArrowRight':
        event.preventDefault();
        newIndex = currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = buttons.length - 1;
        break;
      case 'Enter':
      case ' ':
      case 'Spacebar':
        event.preventDefault();
        this.showTab(currentIndex);
        return;
      default:
        return;
    }

    this.showTab(newIndex);
    const targetButton = buttons[newIndex];
    if (targetButton instanceof HTMLElement) {
      targetButton.focus();
    }
  }

  /**
   * @param {number} index
   */
  showTab(index) {
    const { buttons, panels } = this.#getTabElements();

    if (index < 0 || index >= buttons.length) return;

    buttons.forEach((button, i) => {
      const isActive = i === index;
      button.setAttribute('aria-selected', isActive.toString());
      button.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach((panel, i) => {
      const isActive = i === index;
      if (isActive) {
        panel.classList.add('is-active');
        panel.removeAttribute('hidden');
        panel.setAttribute('aria-hidden', 'false');
      } else {
        panel.classList.remove('is-active');
        panel.setAttribute('hidden', '');
        panel.setAttribute('aria-hidden', 'true');
      }
    });

    this.#persistActiveTab(index);
  }

  #syncFromDom() {
    if (!this.isConnected || this.#isSyncingState) return;

    const { buttons, panels } = this.#getTabElements();
    if (buttons.length === 0 || panels.length === 0) return;

    const selectedIndex = buttons.findIndex((button) => {
      return button.getAttribute('aria-selected') === 'true';
    });

    const targetIndex =
      selectedIndex !== -1 ? selectedIndex : this.#getPreferredTabIndex(this.#getDefaultIndex());

    this.#isSyncingState = true;
    try {
      this.classList.add('tabs-initialized');
      this.showTab(targetIndex);
    } finally {
      this.#isSyncingState = false;
    }
  }

  #getDefaultIndex() {
    let defaultIndex = 0;
    const accordions = this.querySelectorAll('accordion-custom');
    accordions.forEach((accordion, index) => {
      if (accordion.hasAttribute('open-by-default-on-desktop') && defaultIndex === 0) {
        defaultIndex = index;
      }
    });
    return defaultIndex;
  }

  /**
   * @param {number} fallbackIndex
   * @returns {number}
   */
  #getPreferredTabIndex(fallbackIndex) {
    const { buttons } = this.#getTabElements();
    const persistedState = this.#getPersistedActiveTab();

    if (persistedState?.id) {
      const idIndex = buttons.findIndex((button) => button.dataset.tabId === persistedState.id);
      if (idIndex !== -1) return idIndex;
    }

    if (persistedState?.heading) {
      const headingIndex = buttons.findIndex((button) => {
        return button.textContent?.trim() === persistedState.heading;
      });
      if (headingIndex !== -1) return headingIndex;
    }

    if (
      Number.isInteger(persistedState?.index) &&
      persistedState.index >= 0 &&
      persistedState.index < buttons.length
    ) {
      return persistedState.index;
    }

    return fallbackIndex;
  }

  /**
   * @param {number} index
   */
  #persistActiveTab(index) {
    const { buttons } = this.#getTabElements();
    const activeButton = buttons[index];
    if (!activeButton) return;

    const state = {
      id: activeButton.dataset.tabId || '',
      heading: activeButton.textContent?.trim() || '',
      index,
    };

    try {
      sessionStorage.setItem(this.#storageKey, JSON.stringify(state));
    } catch {
      // Ignore storage failures (private mode, restricted storage, etc.)
    }
  }

  #getPersistedActiveTab() {
    try {
      const rawValue = sessionStorage.getItem(this.#storageKey);
      if (!rawValue) return null;
      const state = JSON.parse(rawValue);
      if (!state || typeof state !== 'object') return null;
      return state;
    } catch {
      return null;
    }
  }

  get #storageKey() {
    // Scope persistence to the current PDP URL to avoid cross-product bleed.
    return `${ProductInformationTabs.ACTIVE_TAB_STORAGE_KEY}:${window.location.pathname}`;
  }

  #getTabElements() {
    return {
      buttons: /** @type {HTMLButtonElement[]} */ (Array.from(this.querySelectorAll('.tab-button'))),
      panels: /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.tab-panel'))),
    };
  }
}

if (!customElements.get('product-information-tabs')) {
  customElements.define('product-information-tabs', ProductInformationTabs);
}
